import { Component, forwardRef, inject } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { VmwareModelForStepsHelper } from 'wizards/helpers/vmware-model-helpers/vmware-model-for-steps-helper';
import { VmwarePlanFromStepsHelper } from 'wizards/helpers/vmware-model-helpers/vmware-plan-from-steps-helper';
import { VmwareFormModel } from 'wizards/models/VMWare/vmware-form-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';

import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';

import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';

import { SelectHostStepComponent } from 'wizards/steps/select-host-step/select-host-step.component';

import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';

import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';

import { SimpleScheduleStepComponent } from 'wizards/steps/simple-schedule-step/simple-schedule-step.component';

import { NBFRetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.component';

import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

import { SelectVirtualDisksStepComponent } from 'wizards/steps/select-virtual-disks-step/select-virtual-disks-step.component';

import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';

import { AppAwareStepComponent } from 'wizards/steps/app-aware-step/app-aware-step.component';

import { ConsistencyStepComponent } from 'wizards/steps/archive-consistency-step/consistency-step.component';

import { SelectVirtualMachinesStepComponent } from 'wizards/steps/select-virtual-machines-step/select-virtual-machines-step.component';

import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';

import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';

import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';

import { AdvancedSettingsVirtualStepComponent } from 'wizards/steps/advanced-settings-virtual-step/advanced-settings-virtual-step.component';

import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';

import { AsyncPipe, CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { VMWizardBaseClass } from '../vm-wizard-base.class';

type VMWareWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'destinationPlusRetention'
  | 'connectHost'
  | 'whatToBackUp'
  | 'applicationProcessing'
  | 'selectVirtualDisks'
  | 'advancedSettings'
  | 'backupConsistencyCheck'
  | 'compressionEncryption'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-vmware-wizard',
  templateUrl: './vmware-wizard.component.html',
  providers: [
    {
      provide: VMWizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => VMWareWizardComponent)
    }
  ],
  imports: [
    AdvancedSettingsVirtualStepComponent,
    AppAwareStepComponent,
    AsyncPipe,
    CommonModule,
    CompressionEncryptionStepComponent,
    ConsistencyStepComponent,
    FormsModule,
    NBFRetentionPolicyStepComponent,
    NBFScheduleStepComponent,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectHostStepComponent,
    SelectVirtualDisksStepComponent,
    SelectVirtualMachinesStepComponent,
    SimpleScheduleStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    DestinationPlusRetentionStepComponent
  ]
})
export class VMWareWizardComponent extends VMWizardBaseClass {
  i18nPipe: I18NextPipe;

  public FormModel: VmwareFormModel;
  public hostTitle = this.i18nPipe.transform('wizards:select_host_step_title');
  public helpLink: WizardHelpLink<VMWareWizardHelpDocumentationLinks>;

  constructor() {
    const i18nPipe = inject(I18NextPipe);
    const services = inject(WizardsServicesHubService);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new VmwareFormModel(this.isUnix && !this.isNetBuild, this.isNBF);

    this.initSubscribes();
  }

  protected generateFormModel(plan) {
    this.FormModel = VmwareModelForStepsHelper.VMWareModelFromPlan(plan, this.defaultSettings());

    if (this.isEdit) {
      setTimeout(() => this.storageManager.changeVMStorageHandler(this.getSelectedStorage));
    }
  }

  changeVMWareStorageHandler(connection: StorageConnection): void {
    this.storageManager.changeVMStorageHandler(connection);
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  protected clearAppAwareData(newPlan) {
    VmwarePlanFromStepsHelper.clearAppAwareData(newPlan);
  }

  protected getPlanForSaving(): any {
    return VmwarePlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings());
  }

  getRAWHelpLinks(): Record<VMWareWizardHelpDocumentationLinks, string> {
    return this.isNBF
      ? {
          welcome: 'backup/vmware/vmware#planname',
          whereToBackUp: 'backup/vmware/vmware#storage',
          destinationPlusRetention: 'backup/vmware/vmware#where',
          connectHost: 'backup/vmware/vmware#server',
          whatToBackUp: 'backup/vmware/vmware#vm',
          applicationProcessing: 'backup/vmware/vmware#app',
          selectVirtualDisks: 'backup/vmware/vmware#vdisks',
          advancedSettings: 'backup/vmware/vmware#advanced',
          backupConsistencyCheck: 'backup/vmware/vmware#check',
          compressionEncryption: 'backup/vmware/vmware#compression',
          schedule: 'backup/vmware/vmware#schedule',
          retention: 'backup/vmware/vmware#retention',
          prePost: 'backup/vmware/vmware#prepost',
          notification: 'backup/vmware/vmware#log'
        }
      : {
          welcome: 'backup/vmware/legacy#planname',
          whereToBackUp: 'backup/vmware/legacy#storage',
          destinationPlusRetention: '',
          connectHost: 'backup/vmware/legacy#server',
          whatToBackUp: 'backup/vmware/legacy#vm',
          applicationProcessing: '',
          selectVirtualDisks: 'backup/vmware/legacy#vdisks',
          advancedSettings: 'backup/vmware/legacy#advanced',
          backupConsistencyCheck: '',
          compressionEncryption: 'backup/vmware/legacy#compression',
          schedule: 'backup/vmware/legacy#schedule',
          retention: 'backup/vmware/legacy#retention',
          prePost: 'backup/vmware/legacy#prepost',
          notification: 'backup/vmware/legacy#log'
        };
  }
}
